import React, { useEffect, useState } from "react";
import {
  Button,
  Popover,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AiOutlineEye } from "react-icons/ai";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { server } from "../server";
import Loader from "../components/Layout/Loader";
import { toast } from "react-toastify";

const AdminSellerProducts = () => {
  const { sellerId } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // States for multiple deletion
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [openDeleteMultipleDialog, setOpenDeleteMultipleDialog] = useState(false);

  // Filter and Sort persistence
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);

  useEffect(() => {
    // Load saved filters only if navigating back (not on fresh page load)
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    const isPageReload = navigationEntry && navigationEntry.type === 'reload';
    
    if (!isPageReload) {
      const savedFilter = sessionStorage.getItem('adminSellerProductsFilterModel');
      const savedSort = sessionStorage.getItem('adminSellerProductsSortModel');
      
      if (savedFilter) {
        setFilterModel(JSON.parse(savedFilter));
      }
      if (savedSort) {
        setSortModel(JSON.parse(savedSort));
      }
    } else {
      // Clear filters on page reload
      sessionStorage.removeItem('adminSellerProductsFilterModel');
      sessionStorage.removeItem('adminSellerProductsSortModel');
    }
  }, []);

  const fetchProductsForSeller = async () => {
    try {
      const res = await axios.get(
        `${server}/product/admin-all-products/${sellerId}`,
        { withCredentials: true }
      );
      const products = res.data.products;

      // Fetch seller's name
      const shopRes = await axios.get(
        `${server}/shop/get-shop-info/${sellerId}`
      );
      const sellerName = shopRes.data.shop.name;

      // Add sellerName to each product
      const productsWithSellerName = products.map((product) => ({
        ...product,
        sellerName: sellerName,
      }));

      setData(productsWithSellerName);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      setLoading(false);
      toast.error("Failed to fetch products.");
    }
  };

  useEffect(() => {
    fetchProductsForSeller();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  const StockCell = (params) => {
    const { value, row } = params;
    const [anchorEl, setAnchorEl] = useState(null);
    const [isHovered, setIsHovered] = useState(false);

    const handlePopoverOpen = (event) => {
      setAnchorEl(event.currentTarget);
      setIsHovered(true);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
      setIsHovered(false);
    };

    const open = Boolean(anchorEl);

    return (
      <div
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        style={{
          position: "relative",
          backgroundColor: isHovered ? "#f9f9f9" : "transparent",
          cursor: row.hasVariants ? "pointer" : "default",
          padding: "4px",
        }}
      >
        {value}
        {row.hasVariants && (
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            disableRestoreFocus
            PaperProps={{
              onMouseEnter: handlePopoverOpen,
              onMouseLeave: handlePopoverClose,
              style: {
                maxWidth: "250px",
              },
            }}
          >
            <div style={{ padding: 10, maxHeight: 150, overflowY: "auto" }}>
              {row.variantStock.map((variant, index) => (
                <div key={index}>
                  <strong>{variant.combination}</strong>:{" "}
                  {variant.availability === "All time stock available"
                    ? "All Time Available"
                    : variant.availability === "Out of stock"
                    ? "Out of Stock"
                    : variant.availability === "Limited stock available"
                    ? `Limited (${variant.stock})`
                    : "Unknown Status"}
                </div>
              ))}
            </div>
          </Popover>
        )}
      </div>
    );
  };

  const columns = [
    { field: "id", headerName: "Product ID", minWidth: 130, flex: 0.7 },
    { field: "name", headerName: "Name", minWidth: 150, flex: 1.2 },
    {
      field: "price",
      headerName: "Price",
      minWidth: 100,
      flex: 0.6,
      type: "number",
    },
    {
      field: "Stock",
      headerName: "Stock",
      minWidth: 80,
      flex: 0.5,
      renderCell: (params) => <StockCell {...params} />,
    },
    {
      field: "sold",
      headerName: "Sold",
      type: "number",
      minWidth: 60,
      flex: 0.5,
    },
    {
      field: "category",
      headerName: "Category",
      minWidth: 120,
      flex: 0.7,
    },
    {
      field: "gstPercentage",
      headerName: "GST %",
      minWidth: 80,
      flex: 0.6,
      type: "number",
      editable: true,
      valueParser: (value) => Number(value),
    },
    {
      field: "commissionRate",
      headerName: "Commission %",
      minWidth: 100,
      flex: 0.6,
      type: "number",
      editable: true,
      valueParser: (value) => Number(value),
    },
    {
      field: "Update",
      headerName: "Update",
      flex: 0.5,
      minWidth: 70,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={() => handleUpdate(params.row)}
          variant="contained"
          color="primary"
        >
          Update
        </Button>
      ),
    },
    {
      field: "Preview",
      headerName: "Preview",
      flex: 0.5,
      minWidth: 70,
      sortable: false,
      renderCell: (params) => (
        <Link to={`/product/${params.id}`}>
          <Button size="small">
            <AiOutlineEye size={18} />
          </Button>
        </Link>
      ),
    },
    {
      field: "Delete",
      headerName: "Delete",
      flex: 0.5,
      minWidth: 70,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={() => {
            setProductToDelete(params.row);
            setDeleteDialogOpen(true);
          }}
          color="error"
          variant="contained"
        >
          Delete
        </Button>
      ),
    },
  ];

  const rows = data.map((item) => {
    const getPriceRange = () => {
      if (
        item.quantitySlabsWithCommission &&
        item.quantitySlabsWithCommission.length > 0
      ) {
        const prices = item.quantitySlabsWithCommission
          .map((slab) => slab.discountPriceWithCommission)
          .filter((price) => !isNaN(price));
        if (prices.length > 0) {
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);
          return `Rs ${minPrice.toFixed(2)} - Rs ${maxPrice.toFixed(2)}`;
        }
      }
      return `Rs ${item.discountPrice}`;
    };

    let stockDisplay = "";
    if (item.hasVariants) {
      stockDisplay = "Variant Stock";
    } else {
      if (item.stockAvailability === "All time stock available") {
        stockDisplay = "All Time Available";
      } else if (item.stockAvailability === "Limited stock available") {
        stockDisplay = `Limited (${item.stock})`;
      } else if (item.stockAvailability === "Out of stock") {
        stockDisplay = "Out of Stock";
      } else {
        stockDisplay = item.stock;
      }
    }

    return {
      id: item._id,
      name: item.name,
      price: getPriceRange(),
      Stock: stockDisplay,
      sold: item.sold_out,
      category: item.category,
      gstPercentage: item.gstPercentage,
      commissionRate: item.commissionRate,
      hasVariants: item.hasVariants,
      variantStock: item.variantStock,
      stockStatus: item.stockStatus,
      stock: item.stock,
      stockAvailability: item.stockAvailability,
    };
  });

  const handleUpdate = async (row) => {
    try {
      const updatedProductRes = await axios.put(
        `${server}/product/update-product/${row.id}`,
        {
          commissionRate: row.commissionRate,
          gstPercentage: row.gstPercentage,
        },
        { withCredentials: true }
      );

      const updatedProduct = updatedProductRes.data.product;
      toast.success("Product updated successfully!");
      setData((prevData) =>
        prevData.map((item) =>
          item._id === row.id ? { ...item, ...updatedProduct } : item
        )
      );
    } catch (error) {
      console.error("Error updating product:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to update the product.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      const response = await axios.delete(
        `${server}/product/admin-delete-product/${productToDelete.id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setData((prevData) =>
          prevData.filter((item) => item._id !== productToDelete.id)
        );
      } else {
        toast.error("Failed to delete the product.");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete the product.";
      toast.error(errorMessage);
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const confirmDeleteMultiple = async () => {
    try {
      // Delete selected products in parallel
      await Promise.all(
        selectedProductIds.map((id) =>
          axios.delete(`${server}/product/admin-delete-product/${id}`, {
            withCredentials: true,
          })
        )
      );
      toast.success("Selected products deleted successfully!");
      // Update state by filtering out the deleted products
      setData((prevData) =>
        prevData.filter((item) => !selectedProductIds.includes(item._id))
      );
    } catch (error) {
      console.error("Error deleting multiple products:", error);
      toast.error("An error occurred while deleting products.");
    } finally {
      setOpenDeleteMultipleDialog(false);
      setSelectedProductIds([]);
    }
  };

  const processRowUpdate = (newRow) => {
    setData((prevData) =>
      prevData.map((row) =>
        row._id === newRow.id ? { ...row, ...newRow } : row
      )
    );
    return newRow;
  };

  const handleProcessRowUpdateError = (error) => {
    console.error("Row update error:", error);
    toast.error("Failed to update the row.");
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="w-full mx-8 pt-1 mt-10 bg-white">
      <h2 className="text-2xl font-bold mb-4">
        Products of {data[0]?.sellerName}
      </h2>

      {/* Button for Multiple Deletion */}
      <div className="flex justify-end mb-2">
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            if (selectedProductIds.length === 0) {
              toast.error("No products selected for deletion!");
              return;
            }
            setOpenDeleteMultipleDialog(true);
          }}
        >
          Delete Selected
        </Button>
      </div>

      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={20}
        disableSelectionOnClick
        autoHeight
        checkboxSelection
        rowSelectionModel={selectedProductIds}
        onRowSelectionModelChange={(ids) => setSelectedProductIds(ids)}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        experimentalFeatures={{ newEditingApi: true }}
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) => {
          setFilterModel(newFilterModel);
          sessionStorage.setItem('adminSellerProductsFilterModel', JSON.stringify(newFilterModel));
        }}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          setSortModel(newSortModel);
          sessionStorage.setItem('adminSellerProductsSortModel', JSON.stringify(newSortModel));
        }}
        sx={{
          "& .MuiDataGrid-cell": {
            padding: "6px",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f0f0f0",
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "#f0f0f0",
          },
          "& .MuiButton-root": {
            padding: "4px 8px",
            fontSize: "0.75rem",
          },
        }}
      />

      {/* Single Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the product "
          {productToDelete?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteProduct} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Multiple Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteMultipleDialog}
        onClose={() => setOpenDeleteMultipleDialog(false)}
        aria-labelledby="delete-multiple-dialog-title"
        aria-describedby="delete-multiple-dialog-description"
      >
        <DialogTitle id="delete-multiple-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <p>
            Are you sure you want to delete{" "}
            <strong>{selectedProductIds.length}</strong> selected product
            {selectedProductIds.length > 1 ? "s" : ""}?
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteMultipleDialog(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={confirmDeleteMultiple} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminSellerProducts;
